"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import Card from "@/components/ui/Card";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import Link from "next/link";

interface BlogPost {
  slug: string;
  title: string;
  description: string;
}

interface BlogListProps {
  initialBlogPosts: BlogPost[];
}

export default function BlogList({ initialBlogPosts }: BlogListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [blogPosts, setBlogPosts] = useState(initialBlogPosts);
  const router = useRouter();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);

    const filteredPosts = initialBlogPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(term.toLowerCase()) ||
        post.description.toLowerCase().includes(term.toLowerCase())
    );

    setBlogPosts(filteredPosts);
  };

  return (
    <div>
      <div className="max-w-md mx-auto mb-8">
        <Input
          type="search"
          placeholder="Search blog posts..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full px-4 py-2 rounded-full border-2 border-gray-300  transition duration-300 ease-in-out"
        />
      </div>
      <AnimatePresence>
        {blogPosts?.length > 0 ? (
          <motion.div
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {blogPosts.map((post) => (
              <motion.div
                key={post.slug}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Link href={`/blog/${post.slug}`} passHref>
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300 ease-in-out cursor-pointer custom-class  bg-white shadow-md">
                    <div>
                      <h2 className="text-xl font-semibold mb-2 ">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300">
                        {post.description}
                      </p>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.p
            className="text-center text-gray-500 dark:text-gray-400 mt-8 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            No blog posts found.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
